import React from "react";

import { EmptyState, SectionCard, Skeleton, Stack } from "shared/ui";

import { ChartModal } from "features/Statistics/components/ChartModal";
import { StatsToolbar } from "features/Statistics/components/StatsToolbar";
import { SummaryTiles } from "features/Statistics/components/SummaryTiles";
import { TopCategoriesTable } from "features/Statistics/components/TopCategoriesTable";
import { TopItemsTable } from "features/Statistics/components/TopItemsTable";
import { StatisticsContext } from "features/Statistics/context";
import { useStatisticsState } from "features/Statistics/hooks/useStatisticsState";

import { Container, Message } from "./styles";

const StatisticsScreen = () => {
    const state = useStatisticsState();

    if (state.loading) {
        return (
            <Container>
                <Stack>
                    <Skeleton height="44px" width="420px" />
                    <Skeleton height="160px" />
                    <Skeleton height="280px" />
                </Stack>
            </Container>
        );
    }

    if (state.error) {
        return <Message>Не удалось загрузить статистику</Message>;
    }

    /*
    * Гейтим по самому списку меню, а не по выбранному id: до первого выбора меню проставляется
    * эффектом, то есть кадром позже, и по `menuId === null` экран успевал мигнуть «Нет меню»
    * там, где меню есть.
    * */
    if (state.menuOptions.length === 0) {
        return (
            <Container>
                <SectionCard title="Статистика по заказам">
                    <EmptyState
                        title="Нет меню"
                        description="Статистика считается по заказам меню — сначала должно появиться хотя бы одно."
                    />
                </SectionCard>
            </Container>
        );
    }

    const renderSummary = () => {
        if (state.dataError) return <Message>Не удалось загрузить статистику</Message>;

        // Скелетон вместо пустоты: без него на время запроса плитки мигают нулями.
        if (state.dataLoading && !state.summary) return <Skeleton height="140px" />;

        return <SummaryTiles />;
    };

    const renderTop = (isEmpty: boolean, emptyText: string, table: React.ReactNode) => {
        if (state.dataError) return <Message>Не удалось загрузить отчёт</Message>;

        /*
        * Без итогов периода таблице нечем считать доли — она нарисовала бы «0,0 %» во всех
        * строках. Итоги и топ грузятся разными запросами, поэтому ждём оба.
        * */
        if (state.dataLoading && (isEmpty || !state.summary)) return <Skeleton height="200px" />;

        if (isEmpty || !state.summary) {
            return <EmptyState title="Нет данных" description={emptyText} />;
        }

        return table;
    };

    return (
        <StatisticsContext.Provider value={state}>
            <Container>
                <Stack>
                    <SectionCard
                        title="Статистика по заказам"
                        description="Выручка, заказы и средний чек выбранного меню за период. Нажмите на плитку, чтобы увидеть график"
                        headerRight={<StatsToolbar />}
                    >
                        {renderSummary()}
                    </SectionCard>

                    <SectionCard title="Топ категорий">
                        {renderTop(
                            state.categories.length === 0,
                            "Здесь появятся категории с наибольшим вкладом в выручку.",
                            <TopCategoriesTable />
                        )}
                    </SectionCard>

                    <SectionCard title="Топ позиций">
                        {renderTop(
                            state.items.length === 0,
                            "Здесь появятся самые популярные позиции и их вклад в выручку.",
                            <TopItemsTable />
                        )}
                    </SectionCard>
                </Stack>

                <ChartModal />
            </Container>
        </StatisticsContext.Provider>
    );
};

export const Statistics = StatisticsScreen;
